/**
 * /app/lab/:id — Pulsar LAB strategy dossier (PULSE-200).
 *
 * Full read-only view of one MANUAL lab strategy: a "scoreboard" pitting
 * the in-sample expectation against the live forward-demo result, then
 * five tabs — Обзор (config + filters + thesis), Статистика (equity /
 * weekly / by-symbol charts), Сделки (trade log), Версии (change
 * history), Ошибки (failed orders).
 *
 * Same visual language as the list (PulsarLab.tsx) and the rest of the
 * terminal: bg-ink surfaces, pulse/rose/amber/sky accents, mono
 * tabular-nums, Russo One display. No mutations — pure dossier.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowLeft,
  CheckCircle2,
  CircleSlash,
  Filter,
  GitCommitVertical,
  Radio,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";

import {
  type LabFeedHealth,
  type LabRegimeMatrix,
  type LabSignalDecision,
  type LabStrategyDetail,
  type LabTrade,
  useLabRegime,
  useLabStrategy,
  useLabStrategyFeed,
  useLabStrategySignals,
} from "@/hooks/useDashboardApi";
import { cn } from "@/lib/cn";
import { formatDateTime, formatPct, timeAgo } from "@/lib/format";

type TabKey = "overview" | "stats" | "signals" | "trades" | "versions" | "errors";

export default function PulsarLabStrategy() {
  const { id } = useParams();
  const sid = id ? Number(id) : undefined;
  const { data, isLoading, isError } = useLabStrategy(sid);
  const [tab, setTab] = useState<TabKey>("overview");
  const signals = useLabStrategySignals(sid, tab === "signals");
  const feed = useLabStrategyFeed(sid);
  const regime = useLabRegime(sid);

  return (
    <div className="p-4 sm:p-6">
      <Link
        to="/app/lab"
        className="inline-flex items-center gap-1.5 text-xs text-textDim hover:text-text transition-colors mb-4"
      >
        <ArrowLeft size={13} /> Лаборатория
      </Link>

      {isLoading && (
        <div className="px-5 py-16 text-center text-textFaint text-sm bg-ink border border-line">
          загрузка…
        </div>
      )}
      {isError && (
        <div className="px-5 py-16 text-center text-rose text-sm bg-ink border border-line">
          Стратегия не найдена или недоступна.
        </div>
      )}

      {data && (
        <>
          <Header s={data} feed={feed.data} />
          <Scoreboard s={data} />
          <AccountsBatteryCard s={data} />
          <ReconCard s={data} />
          <RegimeCard m={regime.data} />
          <TabBar s={data} tab={tab} onTab={setTab} />
          <div className="mt-4">
            {tab === "overview" && <OverviewTab s={data} feed={feed.data} />}
            {tab === "stats" && <StatsTab s={data} />}
            {tab === "signals" && <SignalsTab query={signals} />}
            {tab === "trades" && <TradesTab s={data} />}
            {tab === "versions" && <VersionsTab s={data} />}
            {tab === "errors" && <ErrorsTab s={data} />}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── PULSE-230: аккаунты + история battery-вердиктов ───────────────── */

function AccountsBatteryCard({ s }: { s: LabStrategyDetail }) {
  const assigns = s.assignments ?? [];
  const battery = s.battery ?? [];
  if (!assigns.length && !battery.length) return null;
  return (
    <div className="bg-ink border border-line p-4 mb-3 flex flex-wrap gap-x-10 gap-y-3">
      <div>
        <div className="text-2xs uppercase tracking-wider text-textFaint mb-1.5">
          Аккаунты
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {assigns.length ? (
            assigns.map((a) => (
              <span
                key={a.user_id}
                className={cn(
                  "pill font-sans",
                  a.is_active
                    ? a.demo
                      ? "bg-pulse-faint text-pulse"
                      : "bg-fire-faint text-fire"
                    : "bg-elevated text-textFaint line-through",
                )}
                title={a.is_active ? undefined : "линк выключен"}
              >
                {a.label}
                {!a.demo && " · реал"}
              </span>
            ))
          ) : (
            <span className="text-xs text-textFaint">нигде не стоит</span>
          )}
        </div>
      </div>
      {battery.length > 0 && (
        <div>
          <div className="text-2xs uppercase tracking-wider text-textFaint mb-1.5">
            Battery-вердикты (новые → старые)
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {battery.map((b, i) => (
              <span
                key={i}
                className={cn(
                  "pill",
                  b.verdict.startsWith("PASS_PARTIAL")
                    ? "bg-amber-faint text-amber"
                    : b.verdict.startsWith("PASS")
                      ? "bg-pulse-faint text-pulse"
                      : b.verdict.startsWith("FAIL")
                        ? "bg-rose-faint text-rose"
                        : "bg-elevated text-textFaint",
                )}
                title={`exB ${b.ex_best_week ?? "—"} · n=${b.n ?? "—"} · live ${b.live_sum ?? "—"}/${b.live_n ?? "—"}тр · ${formatDateTime(b.checked_at)}`}
              >
                {b.verdict.startsWith("PASS_PARTIAL") ? "PART" : b.verdict.split("_")[0]}
                {b.ex_best_week != null &&
                  ` ${b.ex_best_week >= 0 ? "+" : ""}${b.ex_best_week.toFixed(2)}`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Verdict (mirror of list) ──────────────────────────────────────── */

type Tone = "grey" | "amber" | "pulse" | "rose";
type Verdict = { tone: Tone; label: string; pill: string; bar: string };

function verdictOf(s: LabStrategyDetail): Verdict {
  const closed = s.live.n_closed;
  const expMean = s.expectation?.expected_mean_pp ?? null;
  const liveAvg = s.live.avg_realized_pnl_pct;
  if (closed === 0)
    return { tone: "grey", label: "нет данных", pill: "bg-elevated text-textDim", bar: "bg-textFaint" };
  if (expMean == null)
    return { tone: "amber", label: "нет ожидания", pill: "bg-amber-faint text-amber", bar: "bg-amber" };
  if (closed < 15)
    return { tone: "amber", label: `рано · n=${closed}`, pill: "bg-amber-faint text-amber", bar: "bg-amber" };
  if (liveAvg != null && liveAvg >= expMean)
    return { tone: "pulse", label: "по плану", pill: "bg-pulse-faint text-pulse", bar: "bg-pulse" };
  return { tone: "rose", label: "ниже ожиданий", pill: "bg-rose-faint text-rose", bar: "bg-rose" };
}

/* ─── Header ─────────────────────────────────────────────────────────── */

function Header({ s, feed }: { s: LabStrategyDetail; feed?: LabFeedHealth }) {
  const v = verdictOf(s);
  const dir = s.trade_direction;
  return (
    <header className="bg-ink border border-line border-l-4 border-l-line p-4 mb-3">
      <div className="flex items-center gap-2.5 flex-wrap">
        <span
          className={cn("dot", s.is_active ? "bg-pulse animate-pulse" : "bg-textFaint")}
          title={s.is_active ? "активна" : "выключена"}
        />
        <h1 className="font-display text-xl tracking-tight">{s.name}</h1>
        <DirPill dir={dir} />
        {s.signal_type && <span className="pill bg-elevated text-textDim">{s.signal_type}</span>}
        {s.managed_by === "manual" && (
          <span className="pill bg-sky-faint text-sky" title="ручная стратегия — вне discovery">
            ручная
          </span>
        )}
        {s.managed_by === "discovery" && (
          <span className="pill bg-indigo-faint text-indigo" title="автостратегия — управляется discovery-пайплайном">
            discovery
          </span>
        )}
        {s.archived_at && (
          <span className="pill bg-elevated text-textFaint" title={s.archived_reason ?? undefined}>
            архив
          </span>
        )}
        <FeedBadge feed={feed} />
        <span className={cn("pill ml-auto", v.pill)}>{v.label}</span>
        <span className="text-2xs font-mono text-textFaint">#{s.strategy_id}</span>
      </div>
      {(s.expectation?.thesis_ru ?? s.description) && (
        <p className="mt-2.5 text-xs text-textDim leading-relaxed max-w-3xl">
          {s.expectation?.thesis_ru ?? s.description}
        </p>
      )}
    </header>
  );
}

function DirPill({ dir }: { dir: string | null }) {
  if (!dir) return null;
  const cls =
    dir === "long"
      ? "bg-pulse-faint text-pulse"
      : dir === "short"
        ? "bg-rose-faint text-rose"
        : "bg-elevated text-textDim";
  return <span className={cn("pill", cls)}>{dir === "long" ? "лонг" : dir === "short" ? "шорт" : dir}</span>;
}

/* ─── Feed health (is the signal source alive?) ─────────────────────── */

const FEED_VERDICT: Record<string, { label: string; pill: string; dot: string }> = {
  healthy: { label: "фид ОК", pill: "bg-pulse-faint text-pulse", dot: "bg-pulse" },
  rare: { label: "фид · редкий", pill: "bg-sky-faint text-sky", dot: "bg-sky" },
  stale: { label: "фид · тишина", pill: "bg-amber-faint text-amber", dot: "bg-amber" },
  down: { label: "фид · детектор лёг", pill: "bg-rose-faint text-rose", dot: "bg-rose animate-pulse" },
  retired: { label: "фид · ретайр", pill: "bg-amber-faint text-amber", dot: "bg-amber" },
  orphaned: { label: "фид · потерян", pill: "bg-rose-faint text-rose", dot: "bg-rose animate-pulse" },
  unknown: { label: "фид · ?", pill: "bg-elevated text-textDim", dot: "bg-textFaint" },
};

function FeedBadge({ feed }: { feed?: LabFeedHealth }) {
  if (!feed) return null;
  const v = FEED_VERDICT[feed.verdict] ?? FEED_VERDICT.unknown;
  return (
    <span className={cn("pill", v.pill)} title={feed.reason}>
      {v.label}
    </span>
  );
}

function FeedCard({ feed }: { feed?: LabFeedHealth }) {
  if (!feed) return null;
  const v = FEED_VERDICT[feed.verdict] ?? FEED_VERDICT.unknown;
  return (
    <SectionCard title="Источник сигнала" icon={<Radio size={13} className="text-sky" />}>
      <div className="p-4 space-y-2.5 text-xs">
        <div className="flex items-center gap-2">
          <span className={cn("dot", v.dot)} />
          <span className="text-text">{v.label.replace(/^фид[ ·]*/, "")}</span>
        </div>
        <p className="text-2xs text-textFaint leading-snug">{feed.reason}</p>
        <div className="pt-1 border-t border-line/60 space-y-2">
          <KV label="Тип сигнала" value={feed.signal_type ?? "—"} />
          <KV
            label="Источник"
            value={feed.producer ? `${feed.producer} · ${feed.producer_status}` : "не в реестре"}
          />
          <KV
            label="Последний сигнал"
            value={feed.last_age_h != null ? `${formatAge(feed.last_age_h)} назад` : "—"}
          />
          <KV label="За 7 дней" value={String(feed.count_7d)} />
          <KV
            label="Средний интервал"
            value={feed.avg_interval_h != null ? `~${formatAge(feed.avg_interval_h)}` : "—"}
          />
        </div>
      </div>
    </SectionCard>
  );
}

function formatAge(h: number): string {
  if (h < 1) return `${Math.round(h * 60)}м`;
  if (h < 48) return `${h < 10 ? h.toFixed(1) : Math.round(h)}ч`;
  return `${(h / 24).toFixed(1)}д`;
}

/* ─── Scoreboard — Факт vs Ожидание ─────────────────────────────────── */

function Scoreboard({ s }: { s: LabStrategyDetail }) {
  const v = verdictOf(s);
  const live = s.live;
  const exp = s.expectation;
  const liveAvg = live.avg_realized_pnl_pct;
  const expMean = exp?.expected_mean_pp ?? null;
  const heroClass =
    liveAvg == null
      ? "text-textFaint"
      : expMean == null
        ? "text-amber"
        : liveAvg >= expMean
          ? "text-pulse"
          : "text-rose";

  const frac = exp?.sample_n && exp.sample_n > 0 ? Math.min(live.n_closed / exp.sample_n, 1) : 0;

  return (
    <section className="bg-ink border border-line mb-3 grid grid-cols-2 lg:grid-cols-4 divide-line">
      {/* HERO — live avg/trade vs expected */}
      <div className="col-span-2 lg:col-span-1 p-4 border-b lg:border-b-0 lg:border-r border-line">
        <div className="text-textFaint text-2xs uppercase tracking-wider mb-1.5">
          Факт средн./сделку
        </div>
        <div className={cn("font-mono tabular-nums text-4xl leading-none", heroClass)}>
          {liveAvg != null ? formatPct(liveAvg, { sign: true }) : "—"}
        </div>
        <div className="mt-2 text-2xs font-mono text-textFaint">
          ожидание{" "}
          <span className="text-textDim">
            {expMean != null ? formatPct(expMean, { sign: true }) : "—"}
          </span>
        </div>
      </div>

      {/* Winrate */}
      <div className="p-4 border-b lg:border-b-0 lg:border-r border-line">
        <div className="text-textFaint text-2xs uppercase tracking-wider mb-1.5">Винрейт</div>
        <div className="font-mono tabular-nums text-2xl leading-none text-text">
          {live.win_rate != null ? formatPct(live.win_rate * 100, { digits: 0 }) : "—"}
        </div>
        <div className="mt-2 text-2xs font-mono text-textFaint">
          ожидание{" "}
          <span className="text-textDim">
            {exp?.expected_wr != null ? formatPct(exp.expected_wr * 100, { digits: 0 }) : "—"}
          </span>
        </div>
      </div>

      {/* Total PnL */}
      <div className="p-4 border-b lg:border-b-0 lg:border-r border-line">
        <div className="text-textFaint text-2xs uppercase tracking-wider mb-1.5">
          Итого PnL · live
        </div>
        <div
          className={cn(
            "font-mono tabular-nums text-2xl leading-none",
            live.total_realized_pnl_pct == null
              ? "text-textFaint"
              : live.total_realized_pnl_pct >= 0
                ? "text-pulse"
                : "text-rose",
          )}
        >
          {live.total_realized_pnl_pct != null
            ? formatPct(live.total_realized_pnl_pct, { sign: true })
            : "—"}
        </div>
        <div className="mt-2 text-2xs font-mono text-textFaint">
          закрыто <span className="text-textDim">{live.n_closed}</span> · открыто{" "}
          <span className="text-textDim">{live.n_open}</span>
        </div>
      </div>

      {/* Sample maturity */}
      <div className="p-4">
        <div className="text-textFaint text-2xs uppercase tracking-wider mb-1.5">Выборка</div>
        <div className="font-mono tabular-nums text-2xl leading-none text-text">
          {live.n_closed}
          <span className="text-textFaint text-sm"> / {exp?.sample_n ?? "—"}</span>
        </div>
        <div className="mt-2.5 h-1 bg-elevated overflow-hidden rounded-sm">
          <div
            className={cn("h-full", v.bar)}
            style={{ width: `${Math.max(frac * 100, live.n_closed > 0 ? 3 : 0)}%` }}
          />
        </div>
        <div className="mt-2 text-2xs font-mono text-textFaint">
          посл. сделка {timeAgo(live.last_opened_at)}
        </div>
      </div>
    </section>
  );
}

/* ─── Last trade vs backtest ────────────────────────────────────────── */

function ReconCard({ s }: { s: LabStrategyDetail }) {
  const r = s.last_trade_recon;
  const fmt = (v: number | null | undefined) =>
    v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
  let tone = { pill: "bg-elevated text-textFaint", label: "нет данных" };
  if (r && r.bt_pnl != null && r.divergence_pp != null) {
    const ad = Math.abs(r.divergence_pp);
    if (r.real_reason === "manual" || (r.note ?? "").includes("ручное"))
      tone = { pill: "bg-elevated text-textDim", label: "ручное закрытие (не выход стратегии)" };
    else if (ad < 0.5) tone = { pill: "bg-pulse-faint text-pulse", label: "бектест сходится" };
    else if (ad < 1.5) tone = { pill: "bg-amber-500/15 text-amber-400", label: "расхождение" };
    else tone = { pill: "bg-rose-faint text-rose", label: "крупное расхождение" };
  }
  return (
    <section className="bg-ink border border-line mb-3 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-xs uppercase tracking-wider text-textFaint">Последняя сделка vs бектест</h3>
        <span className={cn("pill ml-auto", tone.pill)}>{tone.label}</span>
      </div>
      {!r || (r.bt_pnl == null && r.symbol == null) ? (
        <p className="text-xs text-textFaint">Ещё нет закрытых сделок для сверки.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 text-sm">
            <ReconCell label="Монета" value={r.symbol ?? "—"} mono />
            <ReconCell label="Аккаунт" value={r.account_label ?? "—"} />
            <ReconCell label="Реал" value={fmt(r.real_pnl)} mono />
            <ReconCell label="Бектест" value={fmt(r.bt_pnl)} mono />
            <ReconCell label="Расхождение Δ" value={fmt(r.divergence_pp)} mono />
            <ReconCell label="Причина реал" value={r.real_reason ?? "—"} />
            <ReconCell label="Причина бт" value={r.bt_reason ?? "—"} />
            <ReconCell label="Совпадение" value={r.reason_match == null ? "—" : r.reason_match ? "да" : "нет"} />
          </div>
          {r.note && <p className="mt-2 text-2xs text-amber-400">⚠ {r.note}</p>}
          {r.computed_at && (
            <p className="mt-1 text-2xs text-textFaint">обновлено {timeAgo(r.computed_at)}</p>
          )}
        </>
      )}
    </section>
  );
}

function ReconCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-wider text-textFaint">{label}</div>
      <div className={cn("text-textDim", mono && "font-mono tabular-nums")}>{value}</div>
    </div>
  );
}

/* ─── Tab bar ────────────────────────────────────────────────────────── */

function TabBar({
  s,
  tab,
  onTab,
}: {
  s: LabStrategyDetail;
  tab: TabKey;
  onTab: (t: TabKey) => void;
}) {
  const tabs: { key: TabKey; label: string; count?: number; warn?: boolean }[] = [
    { key: "overview", label: "Обзор" },
    { key: "stats", label: "Статистика" },
    { key: "signals", label: "Сигналы" },
    { key: "trades", label: "Сделки", count: s.trades.length },
    { key: "versions", label: "Версии", count: s.versions.length },
    { key: "errors", label: "Ошибки", count: s.errors.length, warn: s.errors.length > 0 },
  ];
  return (
    <div className="flex items-center gap-1 border-b border-line overflow-x-auto">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onTab(t.key)}
          className={cn(
            "relative px-3.5 py-2.5 text-xs font-medium whitespace-nowrap transition-colors",
            "border-b-2 -mb-px",
            tab === t.key
              ? "border-fire text-text"
              : "border-transparent text-textDim hover:text-text",
          )}
        >
          {t.label}
          {t.count != null && t.count > 0 && (
            <span
              className={cn(
                "ml-1.5 font-mono tabular-nums text-2xs px-1.5 py-0.5 rounded",
                t.warn ? "bg-rose-faint text-rose" : "bg-elevated text-textDim",
              )}
            >
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ─── Shared layout bits ────────────────────────────────────────────── */

function SectionCard({
  title,
  icon,
  right,
  children,
  className,
}: {
  title?: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("bg-ink border border-line", className)}>
      {title && (
        <header className="flex items-center gap-2 px-4 py-2.5 border-b border-line">
          {icon}
          <h3 className="text-xs font-semibold uppercase tracking-wider text-textDim">{title}</h3>
          {right && <div className="ml-auto">{right}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

function ParamTile({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="px-3 py-2.5 border border-line bg-surface/40">
      <div className="text-textFaint text-2xs uppercase tracking-wider">{label}</div>
      <div className={cn("font-mono tabular-nums text-sm mt-0.5", accent ?? "text-text")}>{value}</div>
    </div>
  );
}

/* ─── Overview tab ───────────────────────────────────────────────────── */

function OverviewTab({ s, feed }: { s: LabStrategyDetail; feed?: LabFeedHealth }) {
  const trailing = s.trailing_stop_enabled
    ? `вкл · arm ${num(s.trailing_stop_activation_pct)} · dist ${num(s.trailing_stop_distance_pct)}`
    : "выкл";
  return (
    <div className="grid lg:grid-cols-3 gap-3">
      {/* Params — 2/3 */}
      <div className="lg:col-span-2 space-y-3">
        <SectionCard title="Параметры исполнения" icon={<ShieldCheck size={13} className="text-sky" />}>
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
            <ParamTile label="Take-Profit" value={pct(s.take_profit_pct)} accent={tpAccent(s.take_profit_pct)} />
            <ParamTile label="Stop-Loss" value={pct(s.stop_loss_pct)} />
            <ParamTile label="Трейлинг" value={trailing} accent={s.trailing_stop_enabled ? "text-pulse" : "text-textDim"} />
            <ParamTile label="Макс. холд" value={mins(s.max_hold_minutes)} />
            <ParamTile label="Кулдаун" value={mins(s.cooldown_minutes)} />
            <ParamTile label="Плечо" value={s.leverage != null ? `${s.leverage}x` : "—"} />
            <ParamTile label="Размер" value={pct(s.position_size_pct)} />
            <ParamTile label="Вход" value={s.entry_type ?? "—"} />
            <ParamTile label="Мин. скор" value={s.min_score != null ? String(s.min_score) : "—"} />
            <ParamTile label="Макс. проскальз." value={pct(s.max_entry_slippage_pct)} />
            <ParamTile
              label="DCA"
              value={s.dca_enabled ? "вкл" : "выкл"}
              accent={s.dca_enabled ? "text-pulse" : "text-textDim"}
            />
            <ParamTile
              label="Серия лоссов"
              value={consecLabel(s.max_consecutive_losses)}
            />
          </div>
          {s.timeframes.length > 0 && (
            <div className="px-3 pb-3 flex items-center gap-2 flex-wrap">
              <span className="text-textFaint text-2xs uppercase tracking-wider">Таймфреймы</span>
              {s.timeframes.map((tf) => (
                <span key={tf} className="pill bg-elevated text-textDim">
                  {tf}
                </span>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Filters + expectation — 1/3 */}
      <div className="space-y-3">
        <FeedCard feed={feed} />

        <SectionCard title="Фильтры входа" icon={<Filter size={13} className="text-amber" />}>
          <FiltersBody filters={s.signal_filters} dirFilter={s.signal_direction_filter} />
        </SectionCard>

        <SectionCard title="Ожидание · in-sample" icon={<GitCommitVertical size={13} className="text-textDim" />}>
          <ExpectationBody s={s} />
        </SectionCard>
      </div>
    </div>
  );
}

const FILTER_LABELS: Record<string, string> = {
  btc_market_phase_filter: "Фаза рынка BTC",
  max_rvol_5m: "Макс. rvol 5m",
  min_rvol_5m: "Мин. rvol 5m",
  max_rvol: "Макс. rvol",
  min_rvol: "Мин. rvol",
  max_rvol_1h: "Макс. rvol 1h",
  min_rvol_1h: "Мин. rvol 1h",
  price_gate_exempt: "Без price-gate",
  min_volume_24h_usd: "Мин. объём 24ч",
  max_atr_pct: "Макс. ATR%",
  min_atr_pct: "Мин. ATR%",
};

function FiltersBody({
  filters,
  dirFilter,
}: {
  filters: Record<string, unknown> | null;
  dirFilter: string | null;
}) {
  const entries = filters ? Object.entries(filters) : [];
  if (entries.length === 0 && !dirFilter) {
    return <div className="px-4 py-5 text-xs text-textFaint">Фильтров нет — берёт все сигналы.</div>;
  }
  return (
    <div className="divide-y divide-line">
      {dirFilter && <FilterRow label="Направление сигнала" value={dirFilter} />}
      {entries.map(([k, val]) => (
        <FilterRow key={k} label={FILTER_LABELS[k] ?? humanize(k)} value={fmtFilterVal(val)} />
      ))}
    </div>
  );
}

function FilterRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-2.5 flex items-baseline justify-between gap-3">
      <span className="text-xs text-textDim">{label}</span>
      <span className="font-mono tabular-nums text-xs text-text text-right">{value}</span>
    </div>
  );
}

function ExpectationBody({ s }: { s: LabStrategyDetail }) {
  const exp = s.expectation;
  if (!exp) return <div className="px-4 py-5 text-xs text-textFaint">Ожидание не задано.</div>;
  return (
    <div className="p-4 space-y-2.5 text-xs">
      <KV label="Средн./сделку" value={exp.expected_mean_pp != null ? formatPct(exp.expected_mean_pp, { sign: true }) : "—"} />
      <KV label="Винрейт" value={exp.expected_wr != null ? formatPct(exp.expected_wr * 100, { digits: 0 }) : "—"} />
      <KV label="Выборка (in-sample)" value={exp.sample_n != null ? String(exp.sample_n) : "—"} />
      <KV label="Окно" value={exp.window ?? "—"} />
      {exp.confidence && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-textDim">Уверенность</span>
          <span className={cn("pill", confidencePill(exp.confidence))}>{confidenceLabel(exp.confidence)}</span>
        </div>
      )}
      {exp.note && <p className="pt-1.5 text-2xs text-textFaint leading-snug border-t border-line/60">{exp.note}</p>}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-textDim">{label}</span>
      <span className="font-mono tabular-nums text-text">{value}</span>
    </div>
  );
}

/* ─── Stats tab ──────────────────────────────────────────────────────── */

function StatsTab({ s }: { s: LabStrategyDetail }) {
  // Equity curve from closed trades (cumulative realized pnl%).
  const equity = useMemo(() => {
    const closed = s.trades
      .filter((t) => !t.is_open && t.realized_pnl_pct != null && t.closed_at)
      .sort((a, b) => new Date(a.closed_at as string).getTime() - new Date(b.closed_at as string).getTime());
    let cum = 0;
    return closed.map((t, i) => {
      cum += t.realized_pnl_pct as number;
      return { i: i + 1, cum: Number(cum.toFixed(2)), symbol: t.symbol };
    });
  }, [s.trades]);

  const weekly = useMemo(
    () =>
      s.weekly.map((w) => ({
        week: w.week.slice(5), // MM-DD
        avg: w.avg_pnl != null ? Number(w.avg_pnl.toFixed(2)) : 0,
        n: w.n,
      })),
    [s.weekly],
  );

  const finalCum = equity.length ? equity[equity.length - 1].cum : 0;
  const curveColor = finalCum >= 0 ? "var(--pulse)" : "var(--rose)";

  return (
    <div className="space-y-3">
      {/* Equity curve */}
      <SectionCard
        title="Кривая результата · live demo"
        right={
          <span className="font-mono tabular-nums text-xs text-textFaint">
            кумулятивный PnL: {equity.length ? formatPct(finalCum, { sign: true }) : "—"}
          </span>
        }
      >
        {equity.length < 2 ? (
          <EmptyMini text="Недостаточно закрытых сделок для кривой." />
        ) : (
          <div className="h-[240px] p-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={equity} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="lab-eq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={curveColor} stopOpacity={0.32} />
                    <stop offset="100%" stopColor={curveColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
                <XAxis
                  dataKey="i"
                  tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "var(--line)" }}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "var(--line)" }}
                  tickLine={false}
                  tickFormatter={(v: number) => `${v}%`}
                  width={44}
                />
                <Tooltip
                  contentStyle={chartTooltip}
                  labelStyle={{ color: "var(--text-faint)" }}
                  itemStyle={{ color: "var(--text)" }}
                  labelFormatter={(l) => `сделка #${l}`}
                  formatter={(v) => [typeof v === "number" ? formatPct(v, { sign: true }) : String(v), "кум. PnL"]}
                />
                <ReferenceLine y={0} stroke="var(--line-hi)" strokeWidth={1} />
                <Area
                  type="monotone"
                  dataKey="cum"
                  stroke={curveColor}
                  strokeWidth={2}
                  fill="url(#lab-eq)"
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      <div className="grid lg:grid-cols-2 gap-3">
        {/* Weekly bars */}
        <SectionCard title="По неделям · средн./сделку">
          {weekly.length === 0 ? (
            <EmptyMini text="Нет закрытых сделок." />
          ) : (
            <div className="h-[220px] p-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekly} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
                  <XAxis
                    dataKey="week"
                    tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    axisLine={{ stroke: "var(--line)" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    axisLine={{ stroke: "var(--line)" }}
                    tickLine={false}
                    tickFormatter={(v: number) => `${v}%`}
                    width={44}
                  />
                  <Tooltip
                    contentStyle={chartTooltip}
                    cursor={{ fill: "var(--elevated)", opacity: 0.4 }}
                    labelStyle={{ color: "var(--text-faint)" }}
                    formatter={(v, _n, p) => [
                      `${typeof v === "number" ? formatPct(v, { sign: true }) : v} · n=${(p?.payload as { n?: number })?.n ?? "—"}`,
                      "средн.",
                    ]}
                  />
                  <ReferenceLine y={0} stroke="var(--line-hi)" strokeWidth={1} />
                  <Bar dataKey="avg" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                    {weekly.map((w, i) => (
                      <Cell key={i} fill={w.avg >= 0 ? "var(--pulse)" : "var(--rose)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>

        {/* By symbol */}
        <SectionCard title="По символам · итого PnL">
          <BySymbol s={s} />
        </SectionCard>
      </div>
    </div>
  );
}

function BySymbol({ s }: { s: LabStrategyDetail }) {
  const rows = s.by_symbol;
  if (rows.length === 0) return <EmptyMini text="Нет закрытых сделок." />;
  const max = Math.max(...rows.map((r) => Math.abs(r.total_pnl ?? 0)), 0.01);
  return (
    <div className="p-3 space-y-1.5 max-h-[220px] overflow-y-auto">
      {rows.map((r) => {
        const tot = r.total_pnl ?? 0;
        const w = (Math.abs(tot) / max) * 100;
        return (
          <div key={r.symbol} className="flex items-center gap-2 text-xs">
            <span className="w-24 shrink-0 font-mono text-textDim truncate" title={r.symbol}>
              {r.symbol}
            </span>
            <div className="flex-1 h-4 bg-elevated/60 rounded-sm overflow-hidden relative">
              <div
                className={cn("h-full", tot >= 0 ? "bg-pulse/70" : "bg-rose/70")}
                style={{ width: `${Math.max(w, 2)}%` }}
              />
            </div>
            <span
              className={cn(
                "w-16 shrink-0 text-right font-mono tabular-nums",
                tot >= 0 ? "text-pulse" : "text-rose",
              )}
            >
              {formatPct(tot, { sign: true })}
            </span>
            <span className="w-8 shrink-0 text-right font-mono tabular-nums text-textFaint">{r.n}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Signals tab — matcher decision feed ────────────────────────────── */

function SignalsTab({ query }: { query: ReturnType<typeof useLabStrategySignals> }) {
  const { data, isLoading, isError } = query;

  if (isLoading)
    return (
      <SectionCard>
        <EmptyMini text="загрузка решений матчера…" />
      </SectionCard>
    );
  if (isError)
    return (
      <SectionCard>
        <EmptyMini text="Не удалось загрузить ленту сигналов." />
      </SectionCard>
    );
  const items = data?.items ?? [];
  if (items.length === 0)
    return (
      <SectionCard>
        <div className="px-4 py-12 flex flex-col items-center gap-2 text-center">
          <Radio size={24} className="text-textFaint" />
          <p className="text-sm text-textDim">Матчер ещё не оценивал сигналы по этой стратегии.</p>
          <p className="text-2xs text-textFaint">Появятся, как только прилетит подходящий сигнал.</p>
        </div>
      </SectionCard>
    );

  const win = data?.window_days ?? 45;

  return (
    <SectionCard
      title="Решения матчера"
      icon={<Radio size={13} className="text-sky" />}
      right={
        <span className="text-2xs text-textFaint">
          итоги за {win}д · ниже последние {items.length}
        </span>
      }
    >
      {/* Summary strip — aggregate totals over the window */}
      <div className="grid grid-cols-3 gap-px bg-line border-b border-line">
        <SigStat label={`Взято (${win}д)`} value={data?.matched ?? 0} accent="text-pulse" />
        <SigStat label="Прошло, не взято" value={data?.passed ?? 0} accent="text-amber" />
        <SigStat label="Отклонено" value={data?.rejected ?? 0} accent="text-textDim" />
      </div>
      <div className="divide-y divide-line/60">
        {items.map((d) => (
          <SignalRow key={d.decision_id} d={d} />
        ))}
      </div>
    </SectionCard>
  );
}

function SigStat({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="bg-ink px-4 py-2.5">
      <div className="text-textFaint text-2xs uppercase tracking-wider">{label}</div>
      <div className={cn("font-mono tabular-nums text-lg", accent)}>{value}</div>
    </div>
  );
}

const SIG_STATUS: Record<string, { label: string; pill: string }> = {
  matched: { label: "взят", pill: "bg-pulse-faint text-pulse" },
  passed: { label: "прошёл, не взят", pill: "bg-amber-faint text-amber" },
  rejected: { label: "отклонён", pill: "bg-elevated text-textDim" },
};

function SignalRow({ d }: { d: LabSignalDecision }) {
  const st = SIG_STATUS[d.status] ?? { label: d.status, pill: "bg-elevated text-textDim" };
  const pnl = d.realized_pnl_pct;
  return (
    <div className="px-4 py-3 hover:bg-elevated/40">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn("pill", st.pill)}>{st.label}</span>
        <span className="font-mono text-xs text-text">{d.symbol}</span>
        <DirPill dir={d.direction} />
        <span className="pill bg-elevated text-textFaint">{d.signal_type}</span>
        {d.timeframe && <span className="text-2xs font-mono text-textFaint">{d.timeframe}</span>}
        {d.score != null && (
          <span className="text-2xs font-mono text-textFaint">score {d.score.toFixed(1)}</span>
        )}
        <span className="ml-auto text-2xs font-mono text-textFaint whitespace-nowrap">
          {formatDateTime(d.decided_at)}
        </span>
      </div>
      {d.reason && d.status !== "matched" && (
        <p className="mt-1 font-mono text-2xs text-textFaint leading-snug break-words">
          {d.reason}
        </p>
      )}
      {d.status === "matched" && (
        <div className="mt-1 flex items-center gap-x-3 text-2xs font-mono text-textFaint flex-wrap">
          <span>
            PnL{" "}
            <span className={pnl == null ? "text-textFaint" : pnl >= 0 ? "text-pulse" : "text-rose"}>
              {pnl != null ? formatPct(pnl, { sign: true }) : "открыта"}
            </span>
          </span>
          {d.realized_exit_reason && (
            <>
              <span className="text-textFaint">·</span>
              <span>{d.realized_exit_reason}</span>
            </>
          )}
          {d.opened_position_id != null && (
            <>
              <span className="text-textFaint">·</span>
              <span>поз #{d.opened_position_id}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Trades tab ─────────────────────────────────────────────────────── */

function TradesTab({ s }: { s: LabStrategyDetail }) {
  if (s.trades.length === 0)
    return (
      <SectionCard>
        <EmptyMini text="Сделок ещё не было." />
      </SectionCard>
    );
  return (
    <SectionCard
      right={<span className="text-2xs text-textFaint">последние {s.trades.length}</span>}
      title="Журнал сделок"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-textFaint text-2xs uppercase tracking-wider border-b border-line">
              <Th>Статус</Th>
              <Th>Символ</Th>
              <Th>Напр.</Th>
              <Th>Открыта</Th>
              <Th>Закрыта</Th>
              <Th className="text-right">Вход</Th>
              <Th className="text-right">PnL</Th>
              <Th>Выход</Th>
            </tr>
          </thead>
          <tbody>
            {s.trades.map((t, i) => (
              <TradeRow key={i} t={t} />
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function TradeRow({ t }: { t: LabTrade }) {
  const pnl = t.is_open ? t.unrealized_pnl_pct : t.realized_pnl_pct;
  return (
    <tr className="border-b border-line/60 hover:bg-elevated/40">
      <Td>
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("dot", t.is_open ? "bg-sky animate-pulse" : "bg-textFaint")} />
          <span className={t.is_open ? "text-sky" : "text-textDim"}>{t.is_open ? "открыта" : "закрыта"}</span>
        </span>
      </Td>
      <Td className="font-mono text-text">{t.symbol}</Td>
      <Td>
        <DirPill dir={t.direction} />
      </Td>
      <Td className="font-mono text-textDim whitespace-nowrap">{formatDateTime(t.opened_at)}</Td>
      <Td className="font-mono text-textDim whitespace-nowrap">{t.is_open ? "—" : formatDateTime(t.closed_at)}</Td>
      <Td className="text-right font-mono text-textDim">{t.entry_price != null ? fmtPrice(t.entry_price) : "—"}</Td>
      <Td className="text-right">
        <span
          className={cn(
            "font-mono tabular-nums",
            pnl == null ? "text-textFaint" : pnl >= 0 ? "text-pulse" : "text-rose",
          )}
        >
          {pnl != null ? formatPct(pnl, { sign: true }) : "—"}
        </span>
      </Td>
      <Td className="text-textFaint">{t.exit_reason ?? (t.is_open ? "—" : "—")}</Td>
    </tr>
  );
}

/* ─── Versions tab ───────────────────────────────────────────────────── */

function VersionsTab({ s }: { s: LabStrategyDetail }) {
  if (s.versions.length === 0)
    return (
      <SectionCard>
        <EmptyMini text="История версий пуста." />
      </SectionCard>
    );
  const top = s.versions[0]?.version_no;
  return (
    <SectionCard title="История версий">
      <ol className="p-4 pl-5 relative">
        <div className="absolute left-[26px] top-6 bottom-6 w-px bg-line" />
        {s.versions.map((v) => {
          const current = v.version_no === top;
          return (
            <li key={v.version_no} className="relative pl-6 pb-4 last:pb-0">
              <span
                className={cn(
                  "absolute left-[-2px] top-0.5 w-3 h-3 rounded-full border-2 border-ink",
                  current ? "bg-fire" : "bg-line-hi",
                )}
              />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm text-text">v{v.version_no}</span>
                {current && <span className="pill bg-fire-faint text-fire">текущая</span>}
                {v.change_source && (
                  <span className="pill bg-elevated text-textFaint">{v.change_source}</span>
                )}
                <span className="ml-auto text-2xs font-mono text-textFaint">
                  {formatDateTime(v.created_at)}
                </span>
              </div>
              {v.change_reason && (
                <p className="mt-1 text-xs text-textDim leading-relaxed">{v.change_reason}</p>
              )}
            </li>
          );
        })}
      </ol>
    </SectionCard>
  );
}

/* ─── Errors tab ─────────────────────────────────────────────────────── */

function ErrorsTab({ s }: { s: LabStrategyDetail }) {
  if (s.errors.length === 0)
    return (
      <SectionCard>
        <div className="px-4 py-12 flex flex-col items-center gap-2 text-center">
          <CheckCircle2 size={26} className="text-pulse" />
          <p className="text-sm text-textDim">Ошибок исполнения нет.</p>
          <p className="text-2xs text-textFaint">Все ордера стратегии прошли без сбоев.</p>
        </div>
      </SectionCard>
    );
  return (
    <SectionCard
      title="Ошибки исполнения"
      icon={<TriangleAlert size={13} className="text-rose" />}
      right={<span className="text-2xs text-textFaint">последние {s.errors.length}</span>}
    >
      <div className="divide-y divide-line/60">
        {s.errors.map((e, i) => (
          <div key={i} className="px-4 py-3">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <CircleSlash size={12} className="text-rose" />
              <span className="font-mono text-xs text-text">{e.symbol ?? "—"}</span>
              {e.status && <span className="pill bg-rose-faint text-rose">{e.status}</span>}
              <span className="ml-auto text-2xs font-mono text-textFaint">
                {formatDateTime(e.created_at)}
              </span>
            </div>
            {e.error_message && (
              <p className="font-mono text-2xs text-rose/90 leading-snug break-all">{e.error_message}</p>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

/* ─── Tiny shared atoms ──────────────────────────────────────────────── */

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-3 py-2 text-left font-medium", className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2 align-middle", className)}>{children}</td>;
}
function EmptyMini({ text }: { text: string }) {
  return <div className="px-4 py-10 text-center text-xs text-textFaint">{text}</div>;
}

const chartTooltip = {
  background: "var(--elevated)",
  border: "1px solid var(--line)",
  borderRadius: 4,
  fontSize: 12,
} as const;

/* ─── helpers ────────────────────────────────────────────────────────── */

function pct(v: number | null | undefined): string {
  return v != null ? `${v}%` : "—";
}
function num(v: number | null | undefined): string {
  return v != null ? `${v}%` : "—";
}
function tpAccent(tp: number | null | undefined): string {
  // tp=0 → SL/trailing-only mode (PULSE-198) — call it out in sky.
  return tp != null && tp <= 0 ? "text-sky" : "text-text";
}
function mins(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1440 && v % 1440 === 0) return `${v / 1440}d`;
  if (v >= 60 && v % 60 === 0) return `${v / 60}h`;
  return `${v}m`;
}
function consecLabel(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 999999) return "выкл";
  if (v === 0) return "выкл";
  return String(v);
}
function fmtPrice(v: number): string {
  if (v >= 1000) return v.toLocaleString("en-US", { maximumFractionDigits: 1 });
  if (v >= 1) return v.toFixed(3);
  return v.toPrecision(4);
}
function humanize(k: string): string {
  return k.replace(/_/g, " ");
}
function fmtFilterVal(v: unknown): string {
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "да" : "нет";
  if (v == null) return "—";
  return String(v);
}
function confidenceLabel(c: string): string {
  switch (c) {
    case "medium":
      return "средняя";
    case "frozen":
      return "заморож.";
    case "low":
      return "низкая";
    case "high":
      return "высокая";
    default:
      return c;
  }
}
function confidencePill(c: string): string {
  switch (c) {
    case "frozen":
      return "bg-sky-faint text-sky";
    case "high":
      return "bg-pulse-faint text-pulse";
    case "low":
      return "bg-rose-faint text-rose";
    default:
      return "bg-amber-faint text-amber";
  }
}

/* ─── Матрица страта×режим (market_state_1m, PULSE-223) ─────────────── */

function RegimeCard({ m }: { m: LabRegimeMatrix | undefined }) {
  if (!m || m.cells.length === 0) return null;
  const feats = Array.from(new Set(m.cells.map((c) => c.feature)));
  return (
    <section className="bg-ink border border-line px-5 py-4 mt-4">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="text-sm font-semibold">Режимы рынка</h2>
        <span className="text-2xs text-textFaint">
          realized-сделки × состояние рынка на входе · тегировано {m.n_tagged} · фл =
          средняя всего флота в том же режиме (60д)
          {m.current && !m.current.stale && " · подсвечен текущий режим"}
        </span>
      </div>
      <div className="space-y-2">
        {feats.map((f) => {
          const cells = m.cells.filter((c) => c.feature === f);
          return (
            <div key={f} className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="w-32 shrink-0 text-textDim">{cells[0].feature_label}</span>
              {cells.map((c) => (
                <span
                  key={c.bucket}
                  title={`n=${c.n}${c.win_rate != null ? ` · WR ${Math.round(c.win_rate * 100)}%` : ""}${
                    c.fleet_avg_pnl != null
                      ? ` · флот ${c.fleet_avg_pnl >= 0 ? "+" : ""}${c.fleet_avg_pnl.toFixed(2)}/сд (n=${c.fleet_n}, 60д)`
                      : ""
                  }`}
                  className={cn(
                    "px-2 py-1 border font-mono tabular-nums",
                    c.is_current ? "border-fire ring-1 ring-fire/50" : "border-line",
                    c.avg_pnl == null
                      ? "text-textFaint"
                      : c.avg_pnl >= 0.3
                        ? "text-mint bg-mint/5"
                        : c.avg_pnl <= -0.3
                          ? "text-rose bg-rose/5"
                          : "text-text",
                  )}
                >
                  {c.bucket}{" "}
                  {c.avg_pnl != null ? `${c.avg_pnl >= 0 ? "+" : ""}${c.avg_pnl.toFixed(2)}` : "·"}
                  <span className="text-textFaint ml-1">n{c.n}</span>
                  {c.fleet_avg_pnl != null && (
                    <span
                      className={cn(
                        "ml-1.5",
                        c.avg_pnl != null && (c.avg_pnl >= 0) !== (c.fleet_avg_pnl >= 0)
                          ? "text-fire"
                          : "text-textFaint",
                      )}
                    >
                      фл {c.fleet_avg_pnl >= 0 ? "+" : ""}
                      {c.fleet_avg_pnl.toFixed(2)}
                    </span>
                  )}
                </span>
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}
